import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { DataTableRowAction } from '@app/types/data-table';
import {
  PackagingActivityRecord,
  PaginatedPackagingActivityRecordList,
} from '@shared/api/types';
import { parseAsInteger, useQueryState } from 'nuqs';
import React from 'react';
import PurchasedPackagingItemPurchasesEditForm from './purchased-packaging-item-purchases-edit-form';
import { usePurchasedPackagingItemPurchasesTableColumns } from './purchased-packaging-item-purchases-table-columns';

interface PurchasedPackagingItemPurchasesTableProps {
  data: PaginatedPackagingActivityRecordList;
  status: 'error' | 'success' | 'loading';
  isLoading: boolean;
  isFetching: boolean;
  isPreviousData: boolean;
  isFetchedAfterMount: boolean;
}

export default function PurchasedPackagingItemPurchasesTable({
  data,
  status,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
}: PurchasedPackagingItemPurchasesTableProps) {
  const [rowAction, setRowAction] =
    React.useState<DataTableRowAction<PackagingActivityRecord> | null>(null);
  const { showSimpleDialog, SimpleDialogComponent } = useSimpleDialog();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { table } = useServerDataTable({
    data: data?.results || [],
    columns: usePurchasedPackagingItemPurchasesTableColumns(),
    shallow: false,
    pageCount,
    initialState: {
      columnVisibility: {
        uuid: false,
      },
      columnOrder: [
        'uuid',
        'period',
        'site',
        'supplier',
        'quantity',
        'totalWeight',
        'unit',
      ],
    },
  });
  return (
    <>
      <DataTable
        table={table}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        className="bg-gray-00 text-left"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          row && setRowAction({ variant: 'update', row });
          showSimpleDialog();
        }}
      >
        <DataTableToolbar table={table} />
      </DataTable>

      {SimpleDialogComponent({
        header: 'Edit purchase',
        contentClassName: 'w-full max-w-3xl',
        open: rowAction?.variant === 'update',
        setOpen: () => setRowAction(null),
        content: (
          <PurchasedPackagingItemPurchasesEditForm
            onOpenChange={() => setRowAction(null)}
            packagingActivityRecord={
              rowAction?.row.original as PackagingActivityRecord
            }
            numberOfPurchases={data?.count || 0}
          />
        ),
      })}
    </>
  );
}
